/**
 * SampleLimiter - Utility class to manage sample product limits
 * Limits customers to a maximum of 6 small samples across all products
 */
class SampleLimiter {
  constructor() {
    this.samplePrefix = 'SAMP-HAND-';
    this.maxSamples = 6;
    this.errorMessage = 'You have reached your free sample limit. Please call the office on 01276 488099 to order more.';
    this.lastErrorShownAt = 0;
    this.errorCooldownMs = 750;
    
    // Bind methods
    this.validateCart = this.validateCart.bind(this);
    this.validateCartUpdate = this.validateCartUpdate.bind(this);
    this.validateAddToCart = this.validateAddToCart.bind(this);
    this.validateVariantAddToCart = this.validateVariantAddToCart.bind(this);
    
    // Initialize event listeners
    this.initEventListeners();
  }
  
  /**
   * Initialize event listeners for cart actions
   */
  initEventListeners() {
    // Listen for cart updates
    document.addEventListener('cart:refresh', this.validateCart);
    
    // Intercept form submissions (add to cart)
    document.addEventListener('submit', (event) => {
      if (event.target.action && event.target.action.includes('/cart/add')) {
        this.validateAddToCart(event);
      }
    }, true);
    
    // Override fetch to intercept AJAX cart additions
    const originalFetch = window.fetch;
    window.fetch = async (...args) => {
      const [resource, config] = args;
      
      // Check if this is a cart update or addition
      if (resource && typeof resource === 'string') {
        // Handle cart/add.js requests
        if (resource.includes('/cart/add.js') || resource.includes('/cart/add')) {
          try {
            const body = config?.body ? 
              (typeof config.body === 'string' ? JSON.parse(config.body) : config.body) : 
              null;
            
            if (body) {
              // Check if this is a sample product by variant ID
              const variantId = body.id;
              const quantity = body.quantity || 1;
              
              // First check if we should skip validation (set by custom event handler)
              const skipValidation = body._skipSampleValidation;
              
              if (!skipValidation) {
                const result = await this.validateVariantAddToCart(variantId, quantity);
                if (!result.valid) {
                  this.showError();
                  // Return a fake successful response to prevent errors, but with our error flag
                  return Promise.resolve(
                    new Response(
                      JSON.stringify({ 
                        status: 422, 
                        message: this.errorMessage,
                        _sampleLimitExceeded: true
                      }),
                      { 
                        status: 200,
                        headers: { 'Content-Type': 'application/json' }
                      }
                    )
                  );
                }
              }
            }
          } catch (error) {
            console.error('Error in cart/add interceptor:', error);
          }
        }
        
        // Handle cart/update or cart/change requests
        if (resource.includes('/cart/update') || resource.includes('/cart/change')) {
          try {
            const body = config?.body ? 
              (typeof config.body === 'string' ? JSON.parse(config.body) : config.body) : 
              null;
            
            if (body) {
              // Skip validation if explicitly set
              if (body._skipSampleValidation) {
                return originalFetch.apply(window, args);
              }
              
              // For line item updates (by line)
              if (body.line !== undefined && body.quantity !== undefined) {
                const result = await this.validateCartUpdate(body.line, body.quantity);
                if (!result.valid) {
                  this.showError();
                  // Return a fake successful response to prevent errors
                  return Promise.resolve(
                    new Response(
                      JSON.stringify({ 
                        status: 422, 
                        message: this.errorMessage,
                        _sampleLimitExceeded: true
                      }),
                      { 
                        status: 200,
                        headers: { 'Content-Type': 'application/json' }
                      }
                    )
                  );
                }
              }
              
              // For updates by ID
              if (body.id !== undefined && body.quantity !== undefined) {
                // Get the cart to check if this is a sample product
                const currentCart = await this.getCart();
                const lineItem = currentCart.items.find(item => item.id === body.id);
                
                if (lineItem && this.isSampleProductBySku(lineItem.sku)) {
                  const sampleCount = this.countSamplesInCart(currentCart, lineItem.id);
                  
                  // If this update would exceed the limit
                  if (sampleCount + body.quantity > this.maxSamples) {
                    this.showError();
                    // Return a fake successful response to prevent errors
                    return Promise.resolve(
                      new Response(
                        JSON.stringify({ 
                          status: 422, 
                          message: this.errorMessage,
                          _sampleLimitExceeded: true
                        }),
                        { 
                          status: 200,
                          headers: { 'Content-Type': 'application/json' }
                        }
                      )
                    );
                  }
                }
              }
            }
          } catch (error) {
            console.error('Error in cart/update interceptor:', error);
          }
        }
      }
      
      // Proceed with the original request
      return originalFetch.apply(window, args);
    };
  }
  
  /**
   * Validate adding a variant to cart
   * @param {string|number} variantId - Variant ID
   * @param {number} quantity - Quantity to add
   * @returns {Promise<Object>} - Validation result
   */
  async validateVariantAddToCart(variantId, quantity) {
    try {
      // Get product data for this variant to check if it's a sample
      const variantData = await this.getVariantData(variantId);
      
      // Check if this is a sample product by SKU
      if (variantData && this.isSampleProductBySku(variantData.sku)) {
        // Get current cart and count samples
        const currentCart = await this.getCart();
        const currentSampleCount = this.countSamplesInCart(currentCart);
        
        // Check if adding this would exceed the limit
        if (currentSampleCount + quantity > this.maxSamples) {
          return { valid: false, message: this.errorMessage };
        }
      }
      
      return { valid: true };
    } catch (error) {
      console.error('Error validating variant add to cart:', error);
      return { valid: true }; // Allow the addition if validation fails
    }
  }
  
  /**
   * Get variant data by ID
   * @param {string|number} variantId - Variant ID
   * @returns {Promise<Object>} - Variant data
   */
  async getVariantData(variantId) {
    try {
      // Try to find the variant in the current cart first (most efficient)
      const cart = await this.getCart();
      const cartItem = cart.items.find(item => item.variant_id == variantId);
      if (cartItem) {
        return {
          sku: cartItem.sku,
          product_id: cartItem.product_id,
          variant_id: cartItem.variant_id
        };
      }
      
      // If not in cart, try to fetch from product.js endpoint
      // Note: This might not work for all Shopify setups and would require
      // knowing the product ID, which we don't have from just the variant ID
      
      // Alternative: maintain a local cache of known sample variants
      // For now, we'll use a simple check based on known pattern
      if (typeof variantId === 'string' && variantId.includes('SAMP-HAND-')) {
        return { sku: variantId, variant_id: variantId };
      }
      
      // Try to fetch the product data (this requires the Shopify AJAX API)
      try {
        const response = await fetch(`/variants/${variantId}.js`, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' }
        });
        
        if (response.ok) {
          return await response.json();
        }
      } catch (e) {
        // Variant API call failed, fall back to checking by ID pattern
        console.warn('Could not fetch variant data, falling back to ID pattern check');
      }
      
      // As a fallback, check if the variant ID matches a pattern for samples
      // This is less reliable but can work if you have a consistent naming convention
      return null;
    } catch (error) {
      console.error('Error getting variant data:', error);
      return null;
    }
  }
  
  /**
   * Validate a cart update before it happens
   * @param {number} line - Cart line number (1-based index)
   * @param {number} quantity - New quantity
   * @returns {Object} - Validation result
   */
  async validateCartUpdate(line, quantity) {
    try {
      const cart = await this.getCart();
      
      // Adjust line to be 0-based index for items array
      const lineIndex = line - 1;
      
      // Check if the item exists in the cart
      if (lineIndex >= 0 && lineIndex < cart.items.length) {
        const item = cart.items[lineIndex];
        
        // If this is a sample product and we're increasing quantity
        if (this.isSampleProductBySku(item.sku) && quantity > item.quantity) {
          // Count current samples excluding this item
          const currentSamplesExcludingThis = this.countSamplesInCart(cart, item.id);
          
          // Check if the new total would exceed the limit
          if (currentSamplesExcludingThis + quantity > this.maxSamples) {
            return { valid: false, message: this.errorMessage };
          }
        }
      }
      
      return { valid: true };
    } catch (error) {
      console.error('Error validating cart update:', error);
      return { valid: true }; // Allow the update if validation fails
    }
  }
  
  /**
   * Validate the cart to ensure sample limits are enforced
   */
  async validateCart() {
    try {
      const cart = await this.getCart();
      const sampleCount = this.countSamplesInCart(cart);
      
      if (sampleCount > this.maxSamples) {
        this.showError();
      }
    } catch (error) {
      console.error('Error validating cart:', error);
    }
  }
  
  /**
   * Validate add to cart form submission
   * @param {Event} event - Form submission event
   */
  async validateAddToCart(event) {
    try {
      const form = event.target;
      const formData = new FormData(form);
      const productId = formData.get('id');
      const quantity = parseInt(formData.get('quantity') || 1);
      
      // Check if this is a sample product
      if (await this.isSampleProduct(productId, this.extractProperties(formData))) {
        const currentCart = await this.getCart();
        const currentSampleCount = this.countSamplesInCart(currentCart);
        
        // If adding this sample would exceed the limit
        if (currentSampleCount + quantity > this.maxSamples) {
          event.preventDefault();
          event.stopPropagation();
          this.showError();
          return false;
        }
      }
    } catch (error) {
      console.error('Error validating add to cart:', error);
    }
  }
  
  /**
   * Extract properties from form data
   * @param {FormData} formData - Form data
   * @returns {Object} - Product properties
   */
  extractProperties(formData) {
    const properties = {};
    
    for (const [key, value] of formData.entries()) {
      if (key.startsWith('properties[')) {
        const propName = key.replace('properties[', '').replace(']', '');
        properties[propName] = value;
      }
    }
    
    return properties;
  }
  
  /**
   * Check if a product is a sample by ID and properties
   * @param {string|number} id - Product ID or variant ID
   * @param {Object} properties - Product properties
   * @returns {Promise<boolean>} - True if this is a sample product
   */
  async isSampleProduct(id, properties) {
    // First check if there are any properties that indicate this is a sample
    if (properties) {
      const propertyValues = Object.values(properties);
      for (const value of propertyValues) {
        if (typeof value === 'string' && 
            (value.includes('sample') || value.includes('SAMP-HAND-'))) {
          return true;
        }
      }
    }
    
    // If no clear indication from properties, try to get variant data
    try {
      const variantData = await this.getVariantData(id);
      if (variantData && variantData.sku) {
        return this.isSampleProductBySku(variantData.sku);
      }
    } catch (e) {
      console.warn('Could not determine if product is a sample by variant data');
    }
    
    // Default to false if we can't determine
    return false;
  }
  
  /**
   * Check if a product is a sample by SKU
   * @param {string} sku - Product SKU
   * @returns {boolean} - True if this is a sample product
   */
  isSampleProductBySku(sku) {
    return sku && typeof sku === 'string' && sku.startsWith(this.samplePrefix);
  }
  
  /**
   * Count the number of samples in the cart
   * @param {Object} cart - Cart object
   * @param {string|number} excludeId - Optional ID to exclude from count
   * @returns {number} - Total sample count
   */
  countSamplesInCart(cart, excludeId = null) {
    let count = 0;
    
    if (cart && cart.items) {
      cart.items.forEach(item => {
        if (this.isSampleProductBySku(item.sku) && item.id !== excludeId) {
          count += item.quantity;
        }
      });
    }
    
    return count;
  }
  
  /**
   * Get the current cart state
   * @returns {Promise<Object>} - Cart object
   */
  async getCart() {
    try {
      const response = await fetch(`${FoxTheme.routes.cart_url}.js`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      return await response.json();
    } catch (error) {
      console.error('Error fetching cart:', error);
      return { items: [] };
    }
  }
  
  /**
   * Show error message to the user
   */
  showError() {
    // Prevent multiple near-simultaneous alerts from overlapping validators.
    const now = Date.now();
    if (now - this.lastErrorShownAt < this.errorCooldownMs) {
      return;
    }
    this.lastErrorShownAt = now;

    // Prefer inline feedback in the samples modal if present.
    const modalError = document.querySelector('#PopupModal-samples .samples--error-message');
    if (modalError) {
      modalError.textContent = this.errorMessage;
      modalError.hidden = false;
      return;
    }

    // Fallback to theme notification if available.
    if (window.FoxTheme && window.FoxTheme.Notification && typeof window.FoxTheme.Notification.show === 'function') {
      window.FoxTheme.Notification.show({
        message: this.errorMessage,
        type: 'error',
        duration: 5000
      });
      return;
    }

    // Final fallback.
    alert(this.errorMessage);
  }
}

// Initialize the sample limiter
document.addEventListener('DOMContentLoaded', () => {
  window.sampleLimiter = new SampleLimiter();
});
